import { fetchFlask } from '../../utils/fetchFlask';
import {
    formatIsoDate,
    getCurrentMonthStart,
    getDayCountInRange,
    getDaysInMonth,
    normalizeDayStart,
    parseBackendDate
} from '../../utils/dateUtils';
import {
    formatDecimal,
    parseNumberPtBr
} from '../../utils/numberUtils';
import { isBlank } from '../../utils/stringFormat';
import {
    CRM_LEADS_INITIAL_DATE_FILTERS,
    CRM_LEADS_INITIAL_FILTERS,
    buildCrmLeadDateFilterPayload,
    fetchCrmLeadsPage
} from './crmLeadsData';
import {
    FASE_OPTIONS,
    ORIGEM_OPTIONS,
    STATUS_OPTIONS,
    VENDEDOR_OPTIONS,
    getDaysSince
} from './crmShared';

export const CRM_DASHBOARD_API = 'crm/dashboard';
export const CRM_MARKETING_DATA_API = 'crm/marketingData';
export const CRM_DASHBOARD_TARGET_REVENUE = 400000;
export const CRM_SELLER_TARGET_REVENUE = 70000;
export const CRM_SELLER_MIN_TARGET_REVENUE = 40000;
export const CRM_AI_AGENT_SELLER_ID = 1;
export const CRM_CONTRACT_PHASE_ID = 4;
export const PENDING_SIGNATURE_LIST_LIMIT = 8;
export const PENDING_SIGNATURE_WARNING_DAYS = 3;
export const PENDING_SIGNATURE_DANGER_DAYS = 7;
export const PENDING_SIGNATURE_WINDOW_DAYS = 60;
const PENDING_SIGNATURE_PAGE_SIZE = 100;
const PENDING_SIGNATURE_EXCLUDED_STATUS_LABELS = ['Cancelado', 'Sem Interesse'];
export const PENDING_SIGNATURE_STATUS_IDS = STATUS_OPTIONS
    .filter((option) => !option.label.startsWith('Vetado') && !PENDING_SIGNATURE_EXCLUDED_STATUS_LABELS.includes(option.label))
    .map((option) => option.id);
export const EMPTY_PENDING_SIGNATURES = {
    items: [],
    total: 0,
    totalTicket: 0
};
export const CRM_DASHBOARD_MIN_DATE = new Date(2025, 11, 1);
export const DASHBOARD_GROUP_KEYS = ['vendedor_id', 'status_id', 'fase_id', 'origem_id'];
export const EMPTY_MARKETING_DATA = [];
export const EMPTY_DASHBOARD_DATA = {
    vendedor_id: [],
    status_id: [],
    fase_id: [],
    origem_id: [],
    tempo_medio_fase: []
};

const buildOptionLabelMap = (options) => new Map(options.map((option) => [option.id, option.label]));

const statusLabels = buildOptionLabelMap(STATUS_OPTIONS);
const faseLabels = buildOptionLabelMap(FASE_OPTIONS);
const origemLabels = buildOptionLabelMap(ORIGEM_OPTIONS);
const vendedorLabels = buildOptionLabelMap(VENDEDOR_OPTIONS);

const sumGroupValue = (rows, field) => rows.reduce((sum, row) => sum + (
    field === 'ticket' ? parseNumberPtBr(row[field]) : row[field]
), 0);

const buildBreakdown = (rows, field, labelMap) => rows.map((row) => {
    const id = row[field];
    const fallbackLabel = id === null || id === undefined ? 'Não informado' : `ID ${id}`;

    return {
        id,
        label: labelMap.get(id) ?? fallbackLabel,
        count: row.quantidade_leads,
        totalTicket: parseNumberPtBr(row.ticket)
    };
}).sort((a, b) => b.count - a.count);

const buildPhaseBreakdown = (rows) => {
    const countsById = new Map(rows.map((row) => [
        row.fase_id,
        {
            count: row.quantidade_leads,
            totalTicket: parseNumberPtBr(row.ticket)
        }
    ]));

    return FASE_OPTIONS.map((option) => {
        const values = countsById.get(option.id);

        return {
            id: option.id,
            label: faseLabels.get(option.id) ?? option.label,
            count: values?.count ?? 0,
            totalTicket: values?.totalTicket ?? 0
        };
    });
};

const toHoursOrNull = (value) => (isBlank(value) ? null : parseNumberPtBr(value, null));

// Backend contract (SET-116): tempo_medio / tempo_mediano already expressed in hours.
const buildPhaseDurations = (rows) => new Map(rows.map((row) => [
    row.fase_id,
    {
        averageHours: toHoursOrNull(row.tempo_medio),
        medianHours: toHoursOrNull(row.tempo_mediano)
    }
]));

const buildFunnelData = (phaseBreakdown, phaseDurations) => phaseBreakdown.map((phase, index) => {
    const cumulativeRows = phaseBreakdown.slice(index);
    const duration = phaseDurations.get(phase.id);

    return {
        id: phase.id,
        label: phase.label,
        count: sumGroupValue(cumulativeRows, 'count'),
        totalTicket: sumGroupValue(cumulativeRows, 'totalTicket'),
        averageHours: duration?.averageHours ?? null,
        medianHours: duration?.medianHours ?? null
    };
});

export const buildDashboardDateRange = () => ({
    startDate: getCurrentMonthStart(),
    endDate: new Date()
});

export const buildDashboardFilters = () => ({
    vendedor_id: [],
    status_id: [],
    fase_id: [],
    origem_id: []
});

export const buildCrmDashboardPayload = ({ dateRange, filters }) => JSON.stringify({
    data_inicio: formatIsoDate(dateRange.startDate),
    data_fim: formatIsoDate(dateRange.endDate),
    vendedor_id: filters.vendedor_id,
    status_id: filters.status_id,
    fase_id: filters.fase_id,
    origem_id: filters.origem_id
});

export const buildMarketingDataPayload = ({ dateRange }) => JSON.stringify({
    data_inicio: formatIsoDate(dateRange.startDate),
    data_fim: formatIsoDate(dateRange.endDate)
});

export const fetchCrmDashboard = async ({ dateRange, filters }) => {
    const response = await fetchFlask(CRM_DASHBOARD_API, buildCrmDashboardPayload({ dateRange, filters }));
    return response.data;
};

export const fetchMarketingData = async ({ dateRange }) => {
    const response = await fetchFlask(CRM_MARKETING_DATA_API, buildMarketingDataPayload({ dateRange }));
    return response.data;
};

export const buildMarketingCostViewModel = ({ marketingData, selectedOriginIds, wonKwh }) => {
    const selectedRows = selectedOriginIds.length === 0
        ? marketingData
        : marketingData.filter((row) => selectedOriginIds.includes(row.origem_id));
    const totalCost = selectedRows.reduce((sum, row) => sum + row.custo_mes, 0);
    const costPerWonKwh = wonKwh > 0 ? totalCost / wonKwh : 0;

    return {
        costPerWonKwh,
        totalCost
    };
};

export const resolveGoalTarget = (sellerIds) => {
    const sellerId = sellerIds.length === 1 ? sellerIds[0] : null;

    if (sellerId === null || sellerId === CRM_AI_AGENT_SELLER_ID) {
        return {
            isIndividual: false,
            sellerId,
            target: CRM_DASHBOARD_TARGET_REVENUE,
            minTarget: null
        };
    }

    return {
        isIndividual: true,
        sellerId,
        target: CRM_SELLER_TARGET_REVENUE,
        minTarget: CRM_SELLER_MIN_TARGET_REVENUE
    };
};

// A meta é mensal, o filtro passou a ser diário (SET-101): a projeção vira o ritmo diário do
// período aplicado ao mês em que ele termina. Para o período padrão (1º do mês até hoje) o
// resultado é o mesmo de antes, quando o divisor era o dia corrente.
export const buildGoalModel = ({ dateRange, wonTicket, target = CRM_DASHBOARD_TARGET_REVENUE }) => {
    const daysInEndMonth = getDaysInMonth(dateRange.endDate);
    const rangeDays = getDayCountInRange(dateRange.startDate, dateRange.endDate);
    const projectedRevenue = rangeDays > 0
        ? (wonTicket * daysInEndMonth) / rangeDays
        : 0;

    return {
        target,
        wonTicket,
        projectedRevenue,
        remainingTicket: Math.max(target - wonTicket, 0),
        projectionRatio: target > 0 ? projectedRevenue / target : 0
    };
};

export const buildGoalProjectionText = ({ dateRange, wonTicket, target }) => {
    const goalModel = buildGoalModel({ dateRange, wonTicket, target });
    const projectionPercent = formatDecimal(goalModel.projectionRatio * 100);

    return `${projectionPercent}% da meta`;
};

export const GOAL_STATUS = {
    below: { key: 'below', label: 'Abaixo do mínimo', tone: 'is-warning' },
    belowTarget: { key: 'belowTarget', label: 'Abaixo da meta', tone: 'is-warning' },
    unlocked: { key: 'unlocked', label: 'Destravado', tone: 'is-positive' },
    reached: { key: 'reached', label: 'Meta batida', tone: 'is-success' }
};

export const getGoalStatus = ({ minTarget, target, wonTicket }) => {
    if (wonTicket >= target) return GOAL_STATUS.reached;
    if (minTarget === null) return GOAL_STATUS.belowTarget;
    return wonTicket >= minTarget ? GOAL_STATUS.unlocked : GOAL_STATUS.below;
};

export const buildSellerGoalViewModel = ({ dateRange, sellerIds, wonTicket, pendingTicket }) => {
    const goalTarget = resolveGoalTarget(sellerIds);
    const goalModel = buildGoalModel({ dateRange, wonTicket, target: goalTarget.target });
    const ratioOf = (value) => (goalTarget.target > 0 ? Math.min(Math.max(value / goalTarget.target, 0), 1) : 0);
    const wonRatio = ratioOf(wonTicket);

    return {
        ...goalTarget,
        ...goalModel,
        pendingTicket,
        remainingToMin: goalTarget.minTarget === null ? 0 : Math.max(goalTarget.minTarget - wonTicket, 0),
        ticketIfSigned: wonTicket + pendingTicket,
        status: getGoalStatus({ minTarget: goalTarget.minTarget, target: goalTarget.target, wonTicket }),
        wonRatio,
        pendingRatio: Math.min(ratioOf(pendingTicket), 1 - wonRatio),
        minRatio: goalTarget.minTarget === null ? null : ratioOf(goalTarget.minTarget)
    };
};

export const buildPendingSignatureDateFilters = () => {
    const contractEnd = normalizeDayStart(new Date());
    const contractStart = new Date(contractEnd);
    contractStart.setDate(contractStart.getDate() - PENDING_SIGNATURE_WINDOW_DAYS);

    return {
        ...CRM_LEADS_INITIAL_DATE_FILTERS,
        contrato_inicio: contractStart,
        contrato_fim: contractEnd
    };
};

export const buildPendingSignatureFilters = (sellerIds) => ({
    ...CRM_LEADS_INITIAL_FILTERS,
    vendedor_id: sellerIds,
    fase_id: [CRM_CONTRACT_PHASE_ID],
    status_id: PENDING_SIGNATURE_STATUS_IDS
});

const getContractTime = (item) => parseBackendDate(item.lead.phase_four_date)?.getTime() ?? Number.POSITIVE_INFINITY;

export const getPendingSignatureTone = (days) => {
    if (days === null) return '';
    if (days > PENDING_SIGNATURE_DANGER_DAYS) return 'is-danger';
    return days > PENDING_SIGNATURE_WARNING_DAYS ? 'is-warning' : '';
};

export const fetchPendingSignatures = async (sellerIds) => {
    const data = await fetchCrmLeadsPage({
        dateFilters: buildCrmLeadDateFilterPayload(buildPendingSignatureDateFilters()),
        filters: buildPendingSignatureFilters(sellerIds),
        page: 1,
        pageSize: PENDING_SIGNATURE_PAGE_SIZE
    });
    const items = (Array.isArray(data.items) ? data.items : [])
        .map((lead) => ({
            lead,
            ticketValue: parseNumberPtBr(lead.ticket),
            daysWaiting: getDaysSince(lead.phase_four_date)
        }))
        .sort((a, b) => getContractTime(a) - getContractTime(b));

    return {
        items,
        total: Number(data.total) || items.length,
        totalTicket: data.totalTicket ?? items.reduce((sum, item) => sum + item.ticketValue, 0)
    };
};

export const buildDashboardViewModel = (dashboardData) => {
    const totalSourceRows = DASHBOARD_GROUP_KEYS
        .map((key) => dashboardData[key])
        .find((rows) => rows.length > 0) ?? [];

    const totalLeads = sumGroupValue(totalSourceRows, 'quantidade_leads');
    const totalTicket = sumGroupValue(totalSourceRows, 'ticket');
    const averageTicket = totalLeads > 0 ? totalTicket / totalLeads : 0;

    const statusBreakdown = buildBreakdown(dashboardData.status_id, 'status_id', statusLabels);
    const originBreakdown = buildBreakdown(dashboardData.origem_id, 'origem_id', origemLabels);
    const sellerBreakdown = buildBreakdown(dashboardData.vendedor_id, 'vendedor_id', vendedorLabels);
    const phaseBreakdown = buildPhaseBreakdown(dashboardData.fase_id);
    const phaseDurations = buildPhaseDurations(dashboardData.tempo_medio_fase ?? []);
    const funnelData = buildFunnelData(phaseBreakdown, phaseDurations);
    const wonPhase = phaseBreakdown.find((item) => item.id === 5);

    return {
        totalLeads,
        totalTicket,
        averageTicket,
        statusBreakdown,
        originBreakdown,
        sellerBreakdown,
        phaseBreakdown,
        funnelData,
        wonCount: wonPhase?.count ?? 0,
        wonKwh: wonPhase?.totalTicket ?? 0,
        maxFunnelValue: Math.max(...funnelData.map((item) => item.count), 1)
    };
};
